import {faPenToSquare, faSliders, faTrash} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Alert, Button, Form, Input, Radio, Tag, Tooltip} from 'antd';
import {ReactNode} from 'react';

import {Locale} from '../../../../../../provider/locale/locale';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {
    ExportTemplateType,
    SystemExportTemplateCodeEnum,
} from '../../../../../../service/reviews/export-constructor/export-constructor-type';
import {CommentsLayoutType} from '../../../../../../service/reviews/reviews-report';
import {classNames} from '../../../../../../util/css';
import {TimeSizeEnum} from '../../../../../../util/format';
import {useFormat} from '../../../../../../util/format-hook/format-hook';
import {normalizeEmails} from '../../../../../../util/string';
import {ReviewsReportNameHint} from '../../../reviews-download-report-popup/reviews-report-name-hint/reviews-report-name-hint';
import {
    commentsLayoutStorageKey,
    getSystemTemplateCode,
    getSystemTemplateDescriptionKey,
    getSystemTemplateHeaderKey,
    getSystemTemplatePlaceholderKey,
    isCatalogRatingsTemplate,
    isLegacyFullExportOldTemplate,
} from '../../reviews-export-constructor-helper';
import {ExportConstructorListFormEnum, ExportConstructorListFormType} from '../../reviews-export-constructor-types';
import {ExportTemplateAdditionalDataFields} from '../export-template-additional-data-fields/export-template-additional-data-fields';

import * as styles from './export-template-list-item.module.scss';

type PropsType = {
    template: ExportTemplateType;
    isSelected: boolean;
    isDownloadDisabled: boolean;
    isCustomExportAvailable: boolean;
    onClone: (template: ExportTemplateType) => void;
    onEdit: (templateId: number) => void;
    onDelete: (template: ExportTemplateType) => void;
};

// eslint-disable-next-line complexity
export function ExportTemplateListItem(props: PropsType): JSX.Element {
    const {template, isSelected, isDownloadDisabled, isCustomExportAvailable, onClone, onEdit, onDelete} = props;

    const {getLocalizedString} = useLocale();
    const {getFormattedDateTime} = useFormat();
    const form = Form.useFormInstance<ExportConstructorListFormType>();

    const systemCode = getSystemTemplateCode(template);
    const headerKey = getSystemTemplateHeaderKey(template);
    const descriptionKey = getSystemTemplateDescriptionKey(template);
    const placeholderKey = getSystemTemplatePlaceholderKey(template);
    const isBaseTemplate = systemCode === SystemExportTemplateCodeEnum.Base;

    const title = template.isSystem && headerKey ? getLocalizedString(headerKey) : template.name;

    function renderBadge(): ReactNode {
        if (template.isSystem) {
            return (
                <Tag className={styles.ExportTemplateListItem_badge}>
                    <Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__BADGE__STANDARD" />
                </Tag>
            );
        }

        return (
            <Tag
                className={classNames(styles.ExportTemplateListItem_badge, styles.ExportTemplateListItem_badge__custom)}
            >
                <Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__BADGE__CUSTOM" />
            </Tag>
        );
    }

    function renderSubtitle(): ReactNode {
        if (template.isSystem && descriptionKey) {
            return <p className={styles.ExportTemplateListItem_description}>{getLocalizedString(descriptionKey)}</p>;
        }

        const updatedAt = getFormattedDateTime(new Date(template.updatedAt), {
            [TimeSizeEnum.day]: '2-digit',
            [TimeSizeEnum.month]: '2-digit',
            [TimeSizeEnum.year]: 'numeric',
        });

        return (
            <p className={styles.ExportTemplateListItem_description}>
                <Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__UPDATED_AT" valueMap={{date: updatedAt}} />
            </p>
        );
    }

    function renderActions(): ReactNode {
        if (!isCustomExportAvailable) {
            return null;
        }

        if (!template.isSystem) {
            return (
                <div className={styles.ExportTemplateListItem_actions}>
                    <Tooltip placement="top" title={<Locale stringKey="BUTTON__EDIT" />}>
                        <Button
                            aria-label="edit"
                            className={styles.ExportTemplateListItem_actionButton}
                            icon={<FontAwesomeIcon icon={faPenToSquare} />}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onEdit(template.id);
                            }}
                            type="text"
                        />
                    </Tooltip>
                    <Tooltip placement="top" title={<Locale stringKey="BUTTON__DELETE" />}>
                        <Button
                            aria-label="remove"
                            className={styles.ExportTemplateListItem_actionButton}
                            icon={<FontAwesomeIcon icon={faTrash} />}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onDelete(template);
                            }}
                            type="text"
                        />
                    </Tooltip>
                </div>
            );
        }

        if (isLegacyFullExportOldTemplate(template)) {
            return null;
        }

        return (
            <div className={styles.ExportTemplateListItem_actions}>
                <Tooltip
                    overlayInnerStyle={{whiteSpace: 'pre-line'}}
                    placement="top"
                    title={<Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__CREATE_FROM_TEMPLATE" />}
                >
                    <Button
                        className={styles.ExportTemplateListItem_actionButton}
                        icon={<FontAwesomeIcon icon={faSliders} />}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onClone(template);
                        }}
                        size="small"
                        type="text"
                    />
                </Tooltip>
            </div>
        );
    }

    return (
        <div
            className={classNames(styles.ExportTemplateListItem, {
                [styles.ExportTemplateListItem__selected]: isSelected,
            })}
        >
            <div className={styles.ExportTemplateListItem_wrapper}>
                <Radio value={template.id} />
                <div className={styles.ExportTemplateListItem_body}>
                    <div className={styles.ExportTemplateListItem_header}>
                        <div className={styles.ExportTemplateListItem_titleBlock}>
                            <div className={styles.ExportTemplateListItem_titleRow}>
                                <span className={styles.ExportTemplateListItem_title}>{title}</span>
                                {renderBadge()}
                            </div>
                            {renderSubtitle()}
                        </div>
                        {renderActions()}
                    </div>
                    {isSelected ? (
                        <div className={styles.ExportTemplateListItem_expanded}>
                            {isDownloadDisabled && !isCatalogRatingsTemplate(template) ? (
                                <Alert
                                    className={styles.ExportTemplateListItem_alert}
                                    message={<Locale stringKey="REVIEWS__DOWNLOAD_REPORT_POPUP__REVIEWS__EMPTY" />}
                                    showIcon
                                    type="warning"
                                />
                            ) : null}
                            {isBaseTemplate ? (
                                <div className={styles.ExportTemplateListItem_checkboxes}>
                                    <Form.Item<ExportConstructorListFormType>
                                        className={styles.ExportTemplateListItem_formItem}
                                        initialValue="rows"
                                        label={
                                            <Locale stringKey="REVIEWS__DOWNLOAD_REPORT_POPUP__COMMENTS_LAYOUT__LABEL" />
                                        }
                                        name={ExportConstructorListFormEnum.CommentsLayout}
                                    >
                                        <Radio.Group
                                            disabled={isDownloadDisabled}
                                            onChange={(event) => {
                                                // eslint-disable-next-line no-restricted-globals
                                                localStorage.setItem(commentsLayoutStorageKey, event.target.value);
                                            }}
                                        >
                                            <Radio value={'rows' satisfies CommentsLayoutType}>
                                                <Locale stringKey="REVIEWS__DOWNLOAD_REPORT_POPUP__COMMENTS_LAYOUT__ROWS" />
                                            </Radio>
                                            <Radio value={'columns' satisfies CommentsLayoutType}>
                                                <Locale stringKey="REVIEWS__DOWNLOAD_REPORT_POPUP__COMMENTS_LAYOUT__COLUMNS" />
                                            </Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                    <ExportTemplateAdditionalDataFields<ExportConstructorListFormType>
                                        disabled={isDownloadDisabled}
                                        reviewsSummaryName={ExportConstructorListFormEnum.WithReviewsSummary}
                                        tagsStatName={ExportConstructorListFormEnum.WithTagsStat}
                                    />
                                </div>
                            ) : null}
                            <Form.Item<ExportConstructorListFormType>
                                className={styles.ExportTemplateListItem_formItem}
                                initialValue=""
                                label={<ReviewsReportNameHint />}
                                name={ExportConstructorListFormEnum.Name}
                            >
                                <Input
                                    disabled={isDownloadDisabled && !isCatalogRatingsTemplate(template)}
                                    placeholder={placeholderKey ? getLocalizedString(placeholderKey) : template.name}
                                />
                            </Form.Item>
                            <Form.Item<ExportConstructorListFormType>
                                className={styles.ExportTemplateListItem_formItem}
                                initialValue=""
                                label={<Locale stringKey="REVIEWS_ANALYSIS__COMPETITORS__EXPORT__EMAILS_LABEL" />}
                                name={ExportConstructorListFormEnum.AdditionalUserEmails}
                            >
                                <Input
                                    disabled={isDownloadDisabled && !isCatalogRatingsTemplate(template)}
                                    onBlur={(event) => {
                                        const normalized = normalizeEmails(event.target.value);

                                        form.setFieldsValue({
                                            [ExportConstructorListFormEnum.AdditionalUserEmails]: normalized,
                                        });
                                    }}
                                    placeholder="email1@example.com, email2@example.com, email3@example.com"
                                />
                            </Form.Item>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
